import { useState, useEffect } from 'react';
import { Landmark, TrendingUp, TrendingDown, Minus, AlertTriangle, Users, Crosshair, Activity, Dice5 } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

const DIR_COLORS = { accumulating: '#10b981', distributing: '#ef4444', neutral: '#6b7280' };
const DIR_ICONS = { accumulating: TrendingUp, distributing: TrendingDown, neutral: Minus };
const PARTY_COLORS = { D: '#3b82f6', R: '#ef4444' };

// Sprint 21.13: VENATOR positioning regime palette
const POSITIONING_COLORS = {
  risk_on: '#10b981',
  neutral: '#6b7280',
  risk_off: '#ef4444',
  rotation_growth: '#3b82f6',
  rotation_value: '#8b5cf6',
  defensive: '#f59e0b',
};
const TIER_COLORS = { core: '#10b981', active: '#3b82f6', monitor: '#6b7280' };
const ALIGNMENT_COLORS = { aligned: '#10b981', partially_aligned: '#f59e0b', misaligned: '#ef4444', unknown: '#6b7280' };
const FRESHNESS_COLORS = { fresh: '#10b981', stale: '#f59e0b', no_data: '#ef4444' };

function FlowStanceCard({ stance, freshness, instCounts }) {
  if (!stance) {
    return (
      <ModuleContainer title="VENATOR — Institutional Positioning" subtitle="Awaiting first cycle">
        <div className="px-4 py-6 text-center">
          <Crosshair className="w-8 h-8 mx-auto text-korinth-muted mb-2" />
          <p className="text-[11px] text-korinth-muted font-mono">VENATOR has not produced a flow_stance yet.</p>
          <p className="text-[10px] text-korinth-muted font-mono mt-1">Cron fires every 4h at :15 UTC. SEC EDGAR ingest runs daily 04:30 UTC.</p>
        </div>
      </ModuleContainer>
    );
  }
  const positioning = stance.overall_positioning || 'neutral';
  const positioningColor = POSITIONING_COLORS[positioning] || '#6b7280';
  const alignment = stance.portfolio_alignment || 'unknown';
  const alignmentColor = ALIGNMENT_COLORS[alignment] || '#6b7280';
  const fresh = stance.data_freshness || 'unknown';
  const freshColor = FRESHNESS_COLORS[fresh] || '#6b7280';
  const ageMs = Date.now() - new Date(stance.as_of + (stance.as_of?.endsWith('Z') ? '' : 'Z')).getTime();
  const ageMin = Math.round(ageMs / 60_000);
  const stale6h = ageMs > 6 * 3_600_000;

  return (
    <ModuleContainer title="VENATOR — Institutional Positioning" subtitle="Phase 2 flows specialist · 4h cadence">
      <div className="space-y-3">
        {/* Header strip */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-1 rounded font-bold"
                style={{ color: positioningColor, backgroundColor: positioningColor + '20', border: `1px solid ${positioningColor}50` }}>
            {positioning.replace('_', ' ')}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-mono text-korinth-muted">CONFIDENCE</span>
            <div className="w-24 h-1.5 bg-korinth-bg rounded-full overflow-hidden">
              <div className="h-full" style={{ width: `${stance.confidence}%`, backgroundColor: positioningColor }} />
            </div>
            <span className="text-[10px] font-mono font-bold text-white">{stance.confidence}%</span>
          </div>
          <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-1 rounded"
                style={{ color: alignmentColor, backgroundColor: alignmentColor + '15' }}>
            book {alignment.replace('_', ' ')}
          </span>
          <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-1 rounded"
                style={{ color: freshColor, backgroundColor: freshColor + '15' }}>
            data: {fresh}
          </span>
          {stale6h && <span className="text-[9px] font-mono px-2 py-1 rounded bg-korinth-warning/15 text-korinth-warning">stance {ageMin}min old</span>}
        </div>

        {/* Narrative */}
        {stance.venator_take && (
          <div className="text-[11px] text-white font-mono italic border-l-2 pl-3" style={{ borderColor: positioningColor }}>
            "{stance.venator_take}"
          </div>
        )}

        {/* Whale buys + sells */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <h4 className="text-[9px] font-mono text-korinth-accent uppercase tracking-wider mb-1">Whale buys ({(stance.whale_buys || []).length})</h4>
            <div className="space-y-1">
              {(stance.whale_buys || []).length === 0 && <p className="text-[10px] font-mono text-korinth-muted italic">No clusters this cycle</p>}
              {(stance.whale_buys || []).slice(0, 5).map((w, i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono">
                  <TrendingUp className="w-3 h-3 text-korinth-accent" />
                  <span className="text-white font-bold">{w.ticker}</span>
                  <span className="text-korinth-muted">·</span>
                  <span className="text-korinth-muted">{(w.institutions || []).slice(0, 2).join(', ')}{(w.institutions || []).length > 2 ? ` +${w.institutions.length - 2}` : ''}</span>
                  <span className="ml-auto text-korinth-accent uppercase text-[8px] font-bold">{w.conviction}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-[9px] font-mono text-korinth-danger uppercase tracking-wider mb-1">Whale sells ({(stance.whale_sells || []).length})</h4>
            <div className="space-y-1">
              {(stance.whale_sells || []).length === 0 && <p className="text-[10px] font-mono text-korinth-muted italic">No clusters this cycle</p>}
              {(stance.whale_sells || []).slice(0, 5).map((w, i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono">
                  <TrendingDown className="w-3 h-3 text-korinth-danger" />
                  <span className="text-white font-bold">{w.ticker}</span>
                  <span className="text-korinth-muted">·</span>
                  <span className="text-korinth-muted">{(w.institutions || []).slice(0, 2).join(', ')}{(w.institutions || []).length > 2 ? ` +${w.institutions.length - 2}` : ''}</span>
                  <span className="ml-auto text-korinth-danger uppercase text-[8px] font-bold">{w.conviction}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dominant themes */}
        {(stance.dominant_themes || []).length > 0 && (
          <div>
            <h4 className="text-[9px] font-mono text-korinth-text uppercase tracking-wider mb-1">Dominant themes</h4>
            <div className="flex flex-wrap gap-1.5">
              {stance.dominant_themes.map((t, i) => (
                <span key={i} className="text-[10px] font-mono px-2 py-1 rounded bg-korinth-bg border border-korinth-border/30">
                  <span className="text-white">{t.theme}</span>
                  <span className="text-korinth-muted ml-1.5">({t.support_count}↑/{t.opposition_count}↓ · {t.conviction})</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Insider pulse */}
        {stance.insider_pulse && (
          <div>
            <h4 className="text-[9px] font-mono text-korinth-text uppercase tracking-wider mb-1">Insider pulse (Form 4)</h4>
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono">
              <span className="px-2 py-1 rounded bg-korinth-bg">net: <span className="text-white font-bold">{stance.insider_pulse.net_action || 'n/a'}</span></span>
              {(stance.insider_pulse.top_buys || []).length > 0 && (
                <span className="text-korinth-accent">↑ {(stance.insider_pulse.top_buys || []).join(',')}</span>
              )}
              {(stance.insider_pulse.top_sells || []).length > 0 && (
                <span className="text-korinth-danger">↓ {(stance.insider_pulse.top_sells || []).join(',')}</span>
              )}
              {stance.insider_pulse.sector_breakdown && (
                <span className="text-korinth-muted italic">{stance.insider_pulse.sector_breakdown}</span>
              )}
            </div>
          </div>
        )}

        {/* Actionable candidates */}
        {(stance.actionable_candidates || []).length > 0 && (
          <div>
            <h4 className="text-[9px] font-mono text-korinth-warning uppercase tracking-wider mb-1">Actionable (≥75% conviction)</h4>
            <div className="space-y-1.5">
              {stance.actionable_candidates.map((c, i) => (
                <div key={i} className="px-2 py-1.5 bg-korinth-warning/5 border border-korinth-warning/20 rounded text-[10px] font-mono">
                  <div className="flex items-center gap-2">
                    <span className="text-korinth-warning font-bold uppercase">{c.action}</span>
                    {c.ticker && <span className="text-white font-bold">{c.ticker}</span>}
                    <span className="text-korinth-muted ml-auto">conv {c.conviction}% · urg {c.urgency}</span>
                  </div>
                  <p className="text-korinth-text mt-1 leading-relaxed">{c.rationale}</p>
                  {c.invalidation && <p className="text-korinth-muted mt-0.5">invalidation: {c.invalidation}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio alignment reason */}
        {stance.portfolio_alignment_reason && (
          <p className="text-[10px] font-mono text-korinth-muted italic">
            <span className="text-korinth-text not-italic font-bold uppercase">{alignment}: </span>
            {stance.portfolio_alignment_reason}
          </p>
        )}

        {/* Footer */}
        <div className="flex flex-wrap gap-3 pt-2 border-t border-korinth-border/30 text-[9px] font-mono text-korinth-muted">
          <span>tracked: {instCounts?.core || 0}c/{instCounts?.active || 0}a/{instCounts?.monitor || 0}m</span>
          {freshness && <span>events: {freshness.total} total · {freshness.last_30d} last 30d · {freshness.last_7d} last 7d</span>}
          <span>cycle: {stance.cycle_trigger || 'cron_4h'}</span>
          <span>as_of: {stance.as_of}</span>
        </div>
      </div>
    </ModuleContainer>
  );
}

// Sprint 21.15: Polymarket anomalies card
const SEVERITY_COLORS = {
  critical: '#ef4444',
  high: '#f59e0b',
  medium: '#eab308',
  low: '#6b7280',
};
const POLY_TIER_COLORS = { core: '#10b981', active: '#3b82f6', monitor: '#6b7280' };
const DIRECTION_LABELS = {
  yes_buy: '↑ YES side accumulating',
  yes_sell: '↓ YES side distributing',
  no_buy: '↑ NO side accumulating',
  no_sell: '↓ NO side distributing',
  bidirectional: '↔ Two-sided flow',
};

function PolymarketAnomaliesCard({ anomalies, freshness, marketCounts }) {
  if (!freshness) return null; // pre-mount
  const hasAnomalies = anomalies && anomalies.length > 0;
  const freshColor = freshness.freshness === 'fresh' ? '#10b981'
    : freshness.freshness === 'stale' ? '#f59e0b'
    : '#ef4444';
  return (
    <ModuleContainer
      title={<span className="flex items-center gap-2">🎲 Polymarket Anomalies <span className="text-[10px] font-mono text-korinth-muted">(ADR-056 · informed-money detector)</span></span>}
      subtitle="z-score volume spikes + probability shifts on user-curated tracked_markets"
    >
      <div className="space-y-3">
        {/* Header strip */}
        <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono">
          <span className="px-2 py-1 rounded bg-korinth-bg" style={{ color: freshColor }}>
            data: {freshness.freshness} {freshness.age_minutes != null ? `(${freshness.age_minutes}min ago)` : ''}
          </span>
          <span className="text-korinth-muted">
            tracked: <span className="text-white">{freshness.tracked_active || 0}</span>
            {marketCounts && (
              <> · <span style={{ color: POLY_TIER_COLORS.core }}>{marketCounts.core || 0}c</span> /
              <span style={{ color: POLY_TIER_COLORS.active }}> {marketCounts.active || 0}a</span> /
              <span style={{ color: POLY_TIER_COLORS.monitor }}> {marketCounts.monitor || 0}m</span></>
            )}
          </span>
          <span className="text-korinth-muted">snapshots: <span className="text-white">{freshness.total_snapshots || 0}</span></span>
          <span className="text-korinth-muted">24h anomalies: <span className="text-white">{freshness.recent_anomalies_24h || 0}</span></span>
        </div>

        {/* Anomaly list or empty state */}
        {!hasAnomalies && (
          <div className="px-4 py-6 text-center">
            <Dice5 className="w-8 h-8 mx-auto text-korinth-muted mb-2" />
            <p className="text-[11px] text-korinth-muted font-mono">No volume anomalies in the last 48h.</p>
            <p className="text-[10px] text-korinth-muted font-mono mt-1">Detection requires ≥14d of baseline + z-score &gt;3 + probability shift &gt;5%. Snapshots accumulate every 30 min.</p>
          </div>
        )}

        {hasAnomalies && (
          <div className="space-y-2">
            {anomalies.map(a => {
              const sevColor = SEVERITY_COLORS[a.severity] || '#6b7280';
              const tierColor = POLY_TIER_COLORS[a.tier] || '#6b7280';
              const dirLabel = DIRECTION_LABELS[a.direction] || a.direction;
              const heldRelated = (a.related_tickers || []).slice(0, 4);
              return (
                <div key={a.id} className="px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded font-bold"
                          style={{ color: sevColor, backgroundColor: sevColor + '20' }}>
                      {a.severity}
                    </span>
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ color: tierColor, backgroundColor: tierColor + '15' }}>
                      {a.tier}
                    </span>
                    <span className="text-[10px] font-mono text-korinth-muted">
                      z={(a.volume_z_score || 0).toFixed(1)}σ · {a.window_hours}h window · {a.category?.replace('_', ' ')}
                    </span>
                    <span className="text-[10px] font-mono ml-auto" style={{ color: sevColor }}>
                      {dirLabel}
                    </span>
                  </div>
                  <p className="text-[11px] font-mono text-white font-bold mt-1.5">{a.title}</p>
                  {a.polymarket_inference && (
                    <p className="text-[10px] font-mono text-korinth-text leading-relaxed mt-1 italic">
                      "{a.polymarket_inference}"
                    </p>
                  )}
                  {a.related_kamal_book_impact && (
                    <p className="text-[10px] font-mono text-korinth-muted mt-1">
                      <span className="text-korinth-accent">book impact:</span> {a.related_kamal_book_impact}
                    </p>
                  )}
                  {heldRelated.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {heldRelated.map(t => (
                        <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-korinth-bg border border-korinth-border/30 text-white">{t}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 text-[8px] font-mono text-korinth-muted">
                    <span>prob_shift: {(a.probability_shift * 100).toFixed(1)}pt</span>
                    <span>·</span>
                    <span>detected: {a.detected_at}</span>
                    {a.alerted ? <span className="ml-auto text-korinth-accent">📱 pushed</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ModuleContainer>
  );
}

function TrackedInstitutionsTierPanel({ tiered }) {
  if (!tiered) return null;
  const tiers = ['core', 'active', 'monitor'];
  return (
    <ModuleContainer title="Tracked Institutions" subtitle="User-curated tier coverage (ADR-049)">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map(tier => {
          const list = tiered[tier] || [];
          const color = TIER_COLORS[tier];
          return (
            <div key={tier}>
              <h4 className="text-[10px] font-mono uppercase tracking-wider mb-2 font-bold flex items-center gap-2"
                  style={{ color }}>
                <span>{tier} ({list.length})</span>
              </h4>
              <div className="space-y-1.5">
                {list.length === 0 && <p className="text-[10px] text-korinth-muted font-mono italic">No institutions in this tier</p>}
                {list.map(i => (
                  <div key={i.cik} className="px-2 py-1.5 bg-korinth-bg rounded border border-korinth-border/30">
                    <div className="text-[11px] font-mono font-bold text-white truncate">{i.name}</div>
                    <div className="text-[9px] font-mono text-korinth-muted truncate">{i.manager} · {i.style} · ${i.aum_billions}B</div>
                    {i.notes && <div className="text-[8px] font-mono text-korinth-muted/70 italic mt-0.5 truncate">{i.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </ModuleContainer>
  );
}

export default function SmartMoneyPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('smart-money'); }, []);

  const [institutions, setInstitutions] = useState([]);
  const [signals, setSignals] = useState([]);
  const [divergences, setDivergences] = useState([]);
  const [congressTrades, setCongressTrades] = useState([]);
  const [clusters, setClusters] = useState({ buys: [], sells: [] });
  const [instMoves, setInstMoves] = useState([]);
  // Sprint 21.13: VENATOR flow stance + tier coverage + ingest freshness
  const [flowStance, setFlowStance] = useState(null);
  const [tieredInstitutions, setTieredInstitutions] = useState(null);
  const [eventFreshness, setEventFreshness] = useState(null);
  // Sprint 21.15: Polymarket anomalies + freshness + tier counts
  const [polyAnomalies, setPolyAnomalies] = useState([]);
  const [polyFreshness, setPolyFreshness] = useState(null);
  const [polyMarketCounts, setPolyMarketCounts] = useState(null);

  useEffect(() => {
    async function load() {
      const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
      setInstitutions(await json('/api/smart-money/institutions') || []);
      setSignals(await json('/api/smart-money/signals') || []);
      setDivergences(await json('/api/smart-money/divergences') || []);
      setCongressTrades(await json('/api/smart-money/congress?days=30') || []);
      setClusters(await json('/api/smart-money/clusters') || { buys: [], sells: [] });

      // Sprint 21.13: VENATOR data
      const stanceWrap = await json('/api/flow/stance');
      setFlowStance(stanceWrap?.stance || null);
      const tieredWrap = await json('/api/flow/institutions');
      setTieredInstitutions(tieredWrap || null);
      const freshness = await json('/api/flow/events/freshness');
      setEventFreshness(freshness || null);

      // Sprint 21.15: Polymarket anomalies + freshness + market counts
      const anomalyWrap = await json('/api/polymarket/anomalies?severity=critical,high,medium&hours=48&limit=10');
      setPolyAnomalies(anomalyWrap?.anomalies || []);
      const polyFresh = await json('/api/polymarket/freshness');
      setPolyFreshness(polyFresh || null);
      const marketsWrap = await json('/api/polymarket/markets');
      setPolyMarketCounts(marketsWrap?.counts || null);

      setInstMoves([]); // legacy — populated when 13F position parser ships
    }
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, []);

  const { impact, track, loading } = usePageTracking('smart-money');
  const hasData = signals.length > 0 || institutions.length > 0 || congressTrades.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Landmark className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Smart Money Feed</h1>
        <span className="text-korinth-muted text-sm">13F filings, congressional trades, insider activity</span>
      </div>

      {/* Sprint 20: Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        {hasData ? (
          <>
            <span><span className="text-korinth-muted">Signals:</span> <span className="text-white font-bold">{signals.length}</span></span>
            <span><span className="text-korinth-muted">Tracked institutions:</span> <span className="text-korinth-accent font-bold">{institutions.length}</span></span>
            <span><span className="text-korinth-muted">Congressional trades (30d):</span> <span className="text-korinth-accent font-bold">{congressTrades.length}</span></span>
            <span><span className="text-korinth-muted">Divergences:</span> <span className="text-korinth-warning font-bold">{divergences.length}</span></span>
          </>
        ) : (
          <span className="text-korinth-muted italic">Connect Quiver / Finnhub data sources in Settings to populate this page.</span>
        )}
      </div>

      {/* Sprint 21.13: VENATOR flow stance — Phase 2 specialist card on top */}
      <FlowStanceCard
        stance={flowStance}
        freshness={eventFreshness}
        instCounts={tieredInstitutions?.counts}
      />

      {/* Sprint 21.13: Tracked institutions tier coverage */}
      <TrackedInstitutionsTierPanel tiered={tieredInstitutions?.by_tier} />

      {/* Sprint 21.15: Polymarket anomalies (informed-money detector) */}
      <PolymarketAnomaliesCard
        anomalies={polyAnomalies}
        freshness={polyFreshness}
        marketCounts={polyMarketCounts}
      />

      {/* Sprint 20: Portfolio impact */}
      <PortfolioImpactPanel topic="Smart Money" {...impact} loading={loading} />

      {/* Smart Money Signals */}
      {signals.length > 0 && (
        <ModuleContainer title="Smart Money Signals" subtitle="Aggregated direction per asset">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {signals.map(s => {
              const Icon = DIR_ICONS[s.direction] || Minus;
              const color = DIR_COLORS[s.direction] || '#6b7280';
              return (
                <div key={s.asset} className="bg-korinth-bg rounded-lg px-3 py-2 border border-korinth-border/30 text-center">
                  <div className="text-sm font-mono font-bold text-white">{s.asset}</div>
                  <Icon className="w-4 h-4 mx-auto my-1" style={{ color }} />
                  <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>{s.direction}</span>
                  <div className="text-[10px] font-mono text-korinth-muted mt-1">Strength: {s.strength}/100</div>
                </div>
              );
            })}
          </div>
        </ModuleContainer>
      )}

      {/* Divergences */}
      {divergences.length > 0 && (
        <ModuleContainer title="Smart Money vs KAIROS Divergences">
          <div className="space-y-2">
            {divergences.map(d => (
              <div key={d.asset} className="flex items-center gap-3 px-4 py-2 bg-korinth-warning/5 border border-korinth-warning/20 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-korinth-warning flex-shrink-0" />
                <span className="text-xs font-mono text-white font-bold">{d.asset}</span>
                <span className="text-[10px] text-korinth-muted">Smart money: <span style={{ color: DIR_COLORS[d.direction] }}>{d.direction}</span></span>
                <span className="text-[10px] text-korinth-muted">KAIROS: <span className={d.kairos_direction === 'buy' ? 'text-korinth-accent' : 'text-korinth-danger'}>{d.kairos_direction}</span></span>
              </div>
            ))}
          </div>
        </ModuleContainer>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Congressional Trades */}
        <ModuleContainer title="Congressional Trades" subtitle={`${congressTrades.length} trades (30 days)`}>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {congressTrades.length > 0 ? congressTrades.map(t => (
              <div key={t.id} className="flex items-center gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                <span className="font-bold" style={{ color: PARTY_COLORS[t.party] || '#6b7280' }}>{t.trader_name}</span>
                <span className={t.transaction_type === 'buy' ? 'text-korinth-accent' : 'text-korinth-danger'}>{t.transaction_type}</span>
                <span className="text-white font-bold">{t.ticker}</span>
                <span className="text-korinth-muted">{t.amount_range || ''}</span>
                <span className="text-korinth-muted ml-auto">{t.trade_date?.slice(5)}</span>
                {t.suspicion_score >= 70 && <span className="text-[8px] px-1 rounded bg-red-500/10 text-red-400">SUS</span>}
              </div>
            )) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">No congressional trades yet — add via POST /api/smart-money/congress/trade</p>}
          </div>
        </ModuleContainer>

        {/* Insider Clusters */}
        <ModuleContainer title="Insider Clusters" subtitle="Multiple insiders buying same stock">
          <div className="space-y-3">
            {clusters.buys?.length > 0 && (
              <div>
                <h4 className="text-[9px] font-mono text-korinth-accent uppercase tracking-wider mb-1">Cluster Buys</h4>
                {clusters.buys.map(c => (
                  <div key={c.ticker} className="flex items-center justify-between px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono mb-1">
                    <span className="text-white font-bold">{c.ticker}</span>
                    <span className="text-korinth-accent">{c.buy_count} insiders</span>
                    <span className="text-korinth-muted">${((c.total_value || 0) / 1000).toFixed(0)}K</span>
                  </div>
                ))}
              </div>
            )}
            {clusters.sells?.length > 0 && (
              <div>
                <h4 className="text-[9px] font-mono text-korinth-danger uppercase tracking-wider mb-1">Cluster Sells</h4>
                {clusters.sells.map(c => (
                  <div key={c.ticker} className="flex items-center justify-between px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono mb-1">
                    <span className="text-white font-bold">{c.ticker}</span>
                    <span className="text-korinth-danger">{c.sell_count} insiders</span>
                    <span className="text-korinth-muted">${((c.total_value || 0) / 1000).toFixed(0)}K</span>
                  </div>
                ))}
              </div>
            )}
            {(!clusters.buys?.length && !clusters.sells?.length) && (
              <p className="text-[10px] text-korinth-muted font-mono text-center py-4">No insider clusters detected yet</p>
            )}
          </div>
        </ModuleContainer>
      </div>

      {/* Legacy 13F filer roster (Sprint 12) — superseded by Tracked Institutions tier panel above (Sprint 21.13) */}
      <ModuleContainer title="13F Filer Roster (legacy)" subtitle={`${institutions.length} institutions in registry`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {institutions.map(i => (
            <div key={i.id} className="flex items-center gap-2 px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-mono font-bold text-white">{i.name}</span>
                <div className="text-[9px] text-korinth-muted font-mono">{i.manager} | {i.style} | ${i.aum_billions}B AUM</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-mono font-bold" style={{ color: i.weight >= 75 ? '#10b981' : i.weight >= 55 ? '#f59e0b' : '#6b7280' }}>{i.weight}</div>
                <div className="text-[7px] text-korinth-muted">weight</div>
              </div>
            </div>
          ))}
        </div>
      </ModuleContainer>

      {/* Sprint 20: Track record */}
      <TrackRecordPanel {...track} loading={loading} metric_name="Smart Money" />
    </div>
  );
}
